import React from 'react';
import { motion } from 'framer-motion';
import styles from './TermsOfService.module.css';
import { 
  FiFileText, FiCheckCircle, FiAlertTriangle, 
  FiShield, FiUserX, FiBriefcase, FiMail,
  FiHome, FiArrowRight, FiLock, FiCreditCard
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  const termsSections = [
    {
      icon: FiCheckCircle,
      title: "Acceptance of Terms",
      content: "By accessing and using BrandVerse Technologies' website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services."
    },
    {
      icon: FiBriefcase,
      title: "Services Description",
      content: "We provide digital solutions including web development, mobile development, enterprise software, e-commerce solutions, AI & ML solutions, and cloud services. All services are subject to availability and may be modified or discontinued at any time."
    },
    {
      icon: FiUserX,
      title: "User Obligations",
      bulletPoints: [
        "Provide accurate and complete information",
        "Maintain the security of your account credentials",
        "Comply with all applicable laws and regulations",
        "Not misuse or abuse our services",
        "Respect intellectual property rights",
        "Not engage in any fraudulent or harmful activities"
      ]
    },
    {
      icon: FiLock,
      title: "Account Security",
      content: "You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account. Notify us immediately of any unauthorized use of your account."
    },
    {
      icon: FiCreditCard,
      title: "Payment Terms",
      bulletPoints: [
        "All payments are processed securely through trusted payment gateways",
        "Fees are quoted in Indian Rupees (INR) unless otherwise specified",
        "Payment is due upon receipt of invoice unless other arrangements are made",
        "Late payments may incur interest charges",
        "Refunds are subject to our refund policy and service agreements"
      ]
    },
    {
      icon: FiShield,
      title: "Intellectual Property",
      content: "All content, trademarks, logos, and intellectual property on our website are owned by BrandVerse Technologies. You may not copy, modify, distribute, or reproduce any content without our express written consent."
    },
    {
      icon: FiAlertTriangle,
      title: "Limitation of Liability",
      content: "To the maximum extent permitted by law, BrandVerse Technologies shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services."
    },
    {
      icon: FiFileText,
      title: "Termination",
      content: "We may terminate or suspend your access immediately, without prior notice, for conduct that violates these Terms or is harmful to other users or our business operations."
    }
  ];

  const effectiveDate = "January 15, 2024";

  return (
    <motion.div 
      className={styles.termsOfService}
      initial="hidden"
      animate="visible"
      variants={pageVariants}
    >
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <motion.div variants={itemVariants} className={styles.heroIcon}>
            <FiFileText />
          </motion.div>
          <motion.h1 variants={itemVariants}>Terms of Service</motion.h1>
          <motion.p variants={itemVariants}>
            Please read these terms carefully before using our services.
          </motion.p>
          <motion.div variants={itemVariants} className={styles.effectiveDate}>
            Effective Date: {effectiveDate}
          </motion.div>
        </div>
      </div>

      <div className={styles.container}>
        {/* Introduction */}
        <motion.div className={styles.introduction} variants={itemVariants}>
          <h2>Welcome to BrandVerse Technologies</h2>
          <p>
            These Terms of Service govern your use of our website, products, and services. By accessing 
            or using any part of our services, you agree to be bound by these terms. Please read them carefully.
          </p>
          <div className={styles.legalBadge}>
            <FiCheckCircle />
            <span>Legally Binding Agreement</span>
          </div>
        </motion.div>

        {/* Terms Sections */}
        <div className={styles.termsGrid}>
          {termsSections.map((section, index) => (
            <motion.div 
              key={index}
              className={styles.termsCard}
              variants={itemVariants}
              whileHover={{ y: -3, transition: { duration: 0.2 } }}
            >
              <div className={styles.termsIcon}>
                <section.icon />
              </div>
              <h3>{section.title}</h3>
              {section.content && <p>{section.content}</p>}
              {section.bulletPoints && (
                <ul className={styles.bulletList}>
                  {section.bulletPoints.map((point, idx) => (
                    <li key={idx}>
                      <FiArrowRight className={styles.bulletIcon} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>

        {/* Disclaimer Section */}
        <motion.div className={styles.disclaimer} variants={itemVariants}>
          <h2>Disclaimer of Warranties</h2>
          <p>
            Our services are provided "as is" without any warranties, express or implied. We do not warrant 
            that the services will be uninterrupted, error-free, or completely secure. We reserve the right 
            to modify, suspend, or discontinue any part of our services at any time.
          </p>
        </motion.div>

        {/* Governing Law */}
        <motion.div className={styles.governingLaw} variants={itemVariants}>
          <h2>Governing Law</h2>
          <p>
            These terms shall be governed by and construed in accordance with the laws of India. Any disputes 
            arising under these terms shall be subject to the exclusive jurisdiction of the courts in Vijayawada, 
            Andhra Pradesh.
          </p>
        </motion.div>

        {/* Changes to Terms */}
        <motion.div className={styles.changes} variants={itemVariants}>
          <h2>Changes to Terms</h2>
          <p>
            We reserve the right to modify these terms at any time. We will notify users of material changes 
            via email or through our website. Your continued use of our services after such modifications 
            constitutes acceptance of the updated terms.
          </p>
        </motion.div>

        {/* Contact Section */}
        <motion.div className={styles.contactSection} variants={itemVariants}>
          <h2>Questions About Terms</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className={styles.contactInfo}>
            <div className={styles.contactCard}>
              <FiMail className={styles.contactIcon} />
              <h4>Legal Department</h4>
              <a href="mailto:contact@brandversetech.com">contact@brandversetech.com</a>
            </div>
            <div className={styles.contactCard}>
              <FiBriefcase className={styles.contactIcon} />
              <h4>Business Inquiries</h4>
              <a href="mailto:contact@brandversetech.com">contact@brandversetech.com</a>
            </div>
          </div>
        </motion.div>

        {/* Back to Home Button */}
        <motion.div className={styles.backHome} variants={itemVariants}>
          <button onClick={() => navigate('/')} className={styles.backButton}>
            <FiHome />
            <span>Back to Home</span>
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default TermsOfService;